import React, { useEffect, useCallback, useState } from "react";
import EditMenu from "../EditMenu";
import CategoryToolbar from "./components/CategoryToolbar";
import CategoryTable from "./components/CategoryTable";
import DeleteCategoryModal from "./components/DeleteCategoryModal";
import LoadingScreen from "../../../components/LoadingScreen";
import CategoryModal from "./components/category-modal/CategoryModal";
import { useCategories } from "./hooks/useCategories";
import { useCategoryActions } from "./hooks/useCategoryActions";
import { useCategoryPageState } from "./hooks/useCategoryPageState";
import { PageTitle } from "src/shared/PageTitle";

const CategoriesPage: React.FC = () => {
  const [businessType, setBusinessType] = useState<string>("");

  const { categories, loading: fetchLoading, totalRecords, fetchCategories } = useCategories();
  const { loading: actionLoading, createCategory, updateCategory, deleteCategory } = useCategoryActions();

  const {
    showDeletedCategories,
    setShowDeletedCategories,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    searchTerm,
    setSearchTerm,
    filteredTerm,
    isAddModalOpen,
    setIsAddModalOpen,
    isEditModalOpen,
    setIsEditModalOpen,
    isDeleteModalOpen,
    setIsDeleteModalOpen,
    selectedCategory,
    setSelectedCategory,
  } = useCategoryPageState();

  const loading = fetchLoading || actionLoading;

  useEffect(() => {
    setBusinessType(sessionStorage.getItem("business_type") || "");
    document.title = "Categories › Settings › iMetanic";
  }, []);

  useEffect(() => {
    if (searchTerm) setPage(0);
  }, [searchTerm, setPage]);

  // Fetch categories whenever page, rowsPerPage, searchTerm, or showDeleted changes
  useEffect(() => {
    fetchCategories(showDeletedCategories, page, rowsPerPage, filteredTerm);
  }, [showDeletedCategories, page, rowsPerPage, filteredTerm, fetchCategories]);

  const handleAddCategory = useCallback(
    async (categoryName: string, type: string) => {
      await createCategory({ CategoryName: categoryName.toUpperCase(), Type: type });
      setIsAddModalOpen(false);
      fetchCategories(showDeletedCategories, page, rowsPerPage, filteredTerm);
    },
    [createCategory, fetchCategories, showDeletedCategories, page, rowsPerPage, filteredTerm, setIsAddModalOpen]
  );

  const handleEditCategory = useCallback(
    async (categoryName: string, type: string) => {
      await updateCategory(selectedCategory.id, { CategoryName: categoryName.toUpperCase(), ...(businessType === "Saloon" && { Type: type }) });
      setIsEditModalOpen(false);
      fetchCategories(showDeletedCategories, page, rowsPerPage, filteredTerm);
    },
    [updateCategory, selectedCategory.id, businessType, fetchCategories, showDeletedCategories, page, rowsPerPage, filteredTerm, setIsEditModalOpen]
  );

  const handleDeleteCategory = useCallback(async () => {
    await deleteCategory(selectedCategory.id);
    setIsDeleteModalOpen(false);
    fetchCategories(showDeletedCategories, page, rowsPerPage, filteredTerm);
  }, [deleteCategory, selectedCategory.id, fetchCategories, showDeletedCategories, page, rowsPerPage, filteredTerm, setIsDeleteModalOpen]);

  const handleEditClick = (id: string, name: string) => {
    if (!showDeletedCategories) {
      const category = categories.find((cat) => cat._id.$oid === id);
      setSelectedCategory({ id, name, type: category?.Type || "" });
      setIsEditModalOpen(true);
    }
  };

  const handleDeleteClick = (id: string, name: string) => {
    if (!showDeletedCategories) {
      setSelectedCategory({ id, name, type: "" });
      setIsDeleteModalOpen(true);
    }
  };

  const menu = { Category: "Category" };

  return (
    // <BusinessLayout businessType={businessType} pageType="category">
    <div className='setting-overall-container'>
      <PageTitle title='- Settings › Categories' />
      <EditMenu editmenu={menu} />

      <div className='category-add-container'>
        <CategoryToolbar
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          showDeleted={showDeletedCategories}
          onShowDeletedChange={setShowDeletedCategories}
          onAddCategory={() => setIsAddModalOpen(true)}
        />
      </div>

      <CategoryTable
        categories={categories}
        page={page}
        rowsPerPage={rowsPerPage}
        totalRecords={totalRecords}
        showDeleted={showDeletedCategories}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
        onEdit={handleEditClick}
        onDelete={handleDeleteClick}
      />

      <CategoryModal open={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} onSubmit={handleAddCategory} businessType={businessType} />

      <CategoryModal
        open={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSubmit={handleEditCategory}
        initialName={selectedCategory.name}
        initialType={selectedCategory.type}
        isEdit
        businessType={businessType}
      />

      <DeleteCategoryModal
        open={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteCategory}
        categoryName={selectedCategory.name}
      />

      {loading && <LoadingScreen />}
    </div>
    // </BusinessLayout>
  );
};

export default CategoriesPage;